import React, { useEffect, useMemo } from 'react';
import { Tabs, Tab, Skeleton } from '@mui/material';
import { Box, Paper, Typography, IconButton, Tooltip, Chip } from 'components/mui';
import { EditIcon, CopyIcon } from 'components/icons';
import { DetailPanel } from 'components/shared/DetailPanel';
import { DetailPanelHeader } from 'components/shared/DetailPanelHeader';
import useCaseDetail from 'containers/pages/cases/hooks/useCaseDetail';
import CaseGeneralInfo from './CaseGeneralInfo';
import { CaseComments } from 'containers/pages/cases/components/caseComments';
import { CaseAttachments } from 'containers/pages/cases/components/caseAttachments';
import { CaseSummary } from 'containers/pages/cases/components/caseSummary';
import CaseActivityLog from './CaseActivityLog';
import { getStatusColor } from 'containers/pages/cases/utils/getStatusColor';

interface CaseDetailPanelProps {
  open: boolean;
  onClose: () => void;
  caseId: number | null;
  onEdit?: (caseData: Record<string, unknown>) => void;
  onDuplicate?: (caseData: Record<string, unknown>) => void;
  onReferenceClick?: (referenceId: number) => void;
}

function CaseDetailPanel({ open, onClose, caseId, onEdit, onDuplicate, onReferenceClick }: CaseDetailPanelProps) {
  const { state, handler } = useCaseDetail();
  const { caseData, loading, activeTab } = state;

  useEffect(() => {
    if (open && caseId !== null) {
      handler.fetchCase(caseId);
    }
  }, [open, caseId, handler.fetchCase]);

  const tabs = useMemo(() => {
    const items = [
      { label: 'General', value: 0 },
      { label: 'Comments', value: 1 },
      { label: 'Attachments', value: 2 },
    ];
    if (caseData?.HasSecureAttachments === 'true') {
      items.push({ label: 'Secure Attachments', value: items.length });
    }
    items.push({ label: 'Activity Log', value: items.length });
    items.push({ label: 'Summary', value: items.length });
    return items;
  }, [caseData?.HasSecureAttachments]);

  const hasSecure = caseData?.HasSecureAttachments === 'true';
  const secureTabIndex = 3;
  const activityLogTabIndex = hasSecure ? 4 : 3;
  const summaryTabIndex = hasSecure ? 5 : 4;

  const handleEdit = () => {
    if (caseData && onEdit) {
      onEdit(caseData as unknown as Record<string, unknown>);
    }
  };

  const handleDuplicate = () => {
    if (caseData && onDuplicate) {
      const duplicateData = {
        ...caseData,
        Id: undefined,
        CreatedOn: undefined,
        UpdatedOn: undefined,
      };
      onDuplicate(duplicateData as unknown as Record<string, unknown>);
    }
  };

  const headerActions = (
    <>
      <Tooltip title="Edit Case">
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Duplicate Case">
        <IconButton size="small" onClick={handleDuplicate}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const statusBadge = caseData ? (
    <Chip label={caseData.StatusTitle} size="small" color={getStatusColor(caseData.StatusTitle)} sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 1 }} />
  ) : undefined;

  return (
    <DetailPanel
      open={open}
      onClose={onClose}
      maxWidth="md"
      header={
        <DetailPanelHeader
          title={caseData?.Title || 'Loading...'}
          subtitle={caseData?.StatusComment || 'No status comment'}
          onClose={onClose}
          statusBadge={statusBadge}
          actions={caseData ? headerActions : undefined}
        />
      }
    >
      {loading ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={200} />
          <Skeleton variant="rounded" height={150} />
        </Box>
      ) : caseData ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => handler.setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {activeTab === 0 && <CaseGeneralInfo caseData={caseData} onReferenceClick={onReferenceClick} />}
            {activeTab === 1 && (
              <Box sx={{ p: 2 }}>
                <CaseComments
                  caseId={caseData.Id}
                  caseTitle={caseData.Title}
                  customerId={caseData.CustomerId}
                  customerName={caseData.CustomerName}
                  groupId={caseData.GroupId}
                  groupTitle={caseData.GroupTitle}
                  projectId={caseData.ProjectId}
                  projectTitle={caseData.ProjectTitle}
                  deliverableId={caseData.DeliverableId}
                  deliverableTitle={caseData.DeliverableTitle}
                />
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ p: 2 }}>
                <CaseAttachments caseId={caseData.Id} customerId={caseData.CustomerId} />
              </Box>
            )}
            {caseData.HasSecureAttachments === 'true' && activeTab === secureTabIndex && (
              <Box sx={{ p: 2 }}>
                <CaseAttachments caseId={caseData.Id} customerId={caseData.CustomerId} secure />
              </Box>
            )}
            {activeTab === activityLogTabIndex && (
              <Box sx={{ p: 2 }}>
                <CaseActivityLog
                  caseId={caseData.Id}
                  createdOn={caseData.CreatedOn}
                  updatedOn={caseData.UpdatedOn}
                />
              </Box>
            )}
            {activeTab === summaryTabIndex && (
              <Box sx={{ p: 2 }}>
                <CaseSummary caseData={caseData} />
              </Box>
            )}
          </Box>
        </>
      ) : state.error ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{state.error}</Typography>
        </Box>
      ) : null}
    </DetailPanel>
  );
}

export default CaseDetailPanel;
